// hooks/useAuthModal.js
import { useModal } from "../context/ModalContext";
import { useAuth } from "../context/AuthProvider";
import { isSignedIn, loadAuth } from "../utils/firebaseAuth";
import dynamic from "next/dynamic";
import { Spinner } from "@jammable/ui-components/Loader";

// Most pages use this hook, but the modal (Formik, yup) is only needed once
// it opens, so it loads then.
const SignInModal = dynamic(() => import("../components/modals/SignInModal"), {
  ssr: false,
  loading: () => (
    <div className="tw-flex tw-items-center tw-justify-center tw-p-8">
      <Spinner size={8} className="tw-border-3" />
    </div>
  ),
});

const useAuthModal = () => {
  const { user } = useAuth();
  const { openModal, closeModal } = useModal();

  const openSignInModal = (onSuccess, title) => {
    // Fetch the Firebase SDK now so the provider buttons work on first click.
    loadAuth().catch(() => {});
    openModal(
      "sign-in-modal",
      // Default to signup: this modal gates actions for logged-out visitors
      // on acquisition surfaces (home hero, SEO hubs), who are mostly new.
      // Returning users are one click away ("Sign in") and the Google button
      // handles both cases identically anyway.
      <SignInModal
        closeModal={() => closeModal("sign-in-modal")}
        onSuccess={onSuccess}
        initialMode="signup"
        title={title}
      />,
    );
  };

  // Returns true and does nothing else if a user is signed in. Otherwise
  // opens the sign-in modal and returns false; if `onSuccess` is provided it
  // runs after a successful sign-in, so the caller's action resumes where the
  // user left off instead of making them click again.
  // `title` (optional) replaces the modal heading, e.g. "Save your cover".
  const checkAuthAndExecute = (onSuccess, title) => {
    // Firebase's answer once it has loaded; before that, AuthProvider's
    // (cached) user, so a returning visitor isn't asked to sign in again.
    if (isSignedIn(user)) {
      return true;
    } else {
      openSignInModal(onSuccess, title);
      return false;
    }
  };

  return { checkAuthAndExecute };
};

export default useAuthModal;
